import { lazy, Suspense, useEffect, useState } from 'react';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router';
import { MotionConfig } from 'motion/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { PublicConfig } from '@medicail/shared';
import { Button, BrandMark, Skeleton, Toaster, TooltipProvider } from '@/components/ui';
import { ConfigContext, fetchPublicConfig } from '@/lib/config';
import { ApiRequestError } from '@/lib/http';
import { AuthProvider } from '@/features/auth/AuthProvider';
import { useAuthContext } from '@/features/auth/useAuth';
import { FullScreenLoader, PublicOnlyLayout, RequireAdmin, RequireAuth } from '@/features/auth/guards';
import SignInPage from '@/features/auth/SignInPage';
import SignUpPage from '@/features/auth/SignUpPage';
import ConfirmPage from '@/features/auth/ConfirmPage';
import ForgotPage from '@/features/auth/ForgotPage';
import AppShell from '@/features/shell/AppShell';
import ChatPage from '@/features/chat/ChatPage';
import NotFound from '@/pages/NotFound';

const AdminRoutes = lazy(() => import('./features/admin'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: (count, err) => !(err instanceof ApiRequestError && err.status < 500) && count < 2,
    },
  },
});

function BootGate({ children }: { children: (cfg: PublicConfig) => React.ReactNode }) {
  const [config, setConfig] = useState<PublicConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    fetchPublicConfig()
      .then((c) => !cancelled && setConfig(c))
      .catch((e: unknown) => !cancelled && setError(e instanceof Error ? e.message : 'Could not reach the server.'));
    return () => {
      cancelled = true;
    };
  }, [attempt]);

  if (config) return <ConfigContext.Provider value={config}>{children(config)}</ConfigContext.Provider>;
  if (error) {
    return (
      <div role="alert" className="grid min-h-dvh place-items-center bg-canvas px-6 text-center">
        <div className="max-w-sm">
          <BrandMark className="mx-auto size-20" />
          <h1 className="mt-6 font-heading text-2xl font-semibold text-ink">We could not reach MedicAiL</h1>
          <p className="mt-2 text-ink-muted">{error}. Check your connection and try again.</p>
          <Button size="lg" className="mt-6" onClick={() => setAttempt((n) => n + 1)}>
            Try again
          </Button>
        </div>
      </div>
    );
  }
  return <FullScreenLoader />;
}

function AdminFallback() {
  return (
    <div className="mx-auto max-w-5xl space-y-4 p-8" aria-busy="true" aria-label="Loading admin dashboard">
      <Skeleton className="h-10 w-64" />
      <Skeleton className="h-40 w-full rounded-3xl" />
      <Skeleton className="h-64 w-full rounded-3xl" />
    </div>
  );
}

function AppRoutes() {
  const { mode } = useAuthContext();
  return (
    <Routes>
      <Route element={<PublicOnlyLayout />}>
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/signup" element={mode === 'dev' ? <Navigate to="/signin" replace /> : <SignUpPage />} />
        <Route path="/confirm" element={mode === 'dev' ? <Navigate to="/signin" replace /> : <ConfirmPage />} />
        <Route path="/forgot" element={mode === 'dev' ? <Navigate to="/signin" replace /> : <ForgotPage />} />
      </Route>
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<ChatPage />} />
        <Route path="/c/:chatId" element={<ChatPage />} />
      </Route>
      <Route
        path="/admin/*"
        element={
          <RequireAdmin>
            <Suspense fallback={<AdminFallback />}>
              <AdminRoutes />
            </Suspense>
          </RequireAdmin>
        }
      />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <MotionConfig reducedMotion="user">
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <BootGate>
            {(config) => (
              <BrowserRouter>
                <AuthProvider config={config}>
                  <AppRoutes />
                </AuthProvider>
              </BrowserRouter>
            )}
          </BootGate>
          <Toaster />
        </TooltipProvider>
      </QueryClientProvider>
    </MotionConfig>
  );
}
